import React, {useState, useEffect, createContext, useContext} from "react";
import {clearCookie, getCookie, setCookie} from "helpers/common";
import Toast from "components/molecules/Toast";
import adminService from "services/adminService";
import authService from "services/authService";
import {useCancellablePromise} from "helpers/promiseHandler";
import {useDispatch, useSelector} from "react-redux";
import {clearState, setBusinessId, setDefaultStore, setUserType, setUser} from "store/reducers/businessSlice";
import {setSubscriptionData, clearSubscriptionData} from "store/reducers/subscriptionSlice";
import subscriptionService from "services/subscriptionService";

const context = {};

export const AuthContext = createContext(context);

export function AuthContextProvider(props) {
    const [isLoggedIn, setIsLoggedIn] = useState(!!getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE));
    const [loading, setLoading] = useState(false);
    const [selectedStore, setSelectedStore] = useState();
    const [loading_user, setLoadingUser] = useState(false);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const {cancellablePromise} = useCancellablePromise();
    const [reFetch, setRefetch] = useState(false);
    const [allowedPages, setAllowedPages] = useState(
        JSON.parse(getCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE)) || []
    );
    const [dashboardModal, setDashboardModal] = useState(false);
    const dispatch = useDispatch();

    // Check for stale data on app initialization
    useEffect(() => {
        const token = getCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);
        if (!token && (sessionStorage.getItem("userEmail") || localStorage.getItem("foodie_rememberMe"))) {
            console.log("AuthContext - Detected stale data without valid token, cleaning up");
            // Clear stale data if no valid token exists
            sessionStorage.clear();
            localStorage.removeItem("foodie_rememberMe");
            localStorage.removeItem("persist:root");
        }
    }, []);

    // Get user data from Redux instead of local state
    const user = useSelector(state => state.business.user);

    // Function to check subscription status using dedicated endpoint
    const checkSubscriptionStatus = async () => {
        try {
            console.log("AuthContext - Checking subscription status via API");
            const response = await subscriptionService.getSubscriptionStatus();
            console.log("AuthContext - Subscription API response:", response);

            const subscriptionActive = response?.canAccess === true || response?.hasActiveSubscription === true;

            // Debug logging
            console.log("AuthContext - canAccess:", response?.canAccess);
            console.log("AuthContext - hasActiveSubscription:", response?.hasActiveSubscription);
            console.log("AuthContext - subscription status:", response?.subscription?.status);
            console.log("AuthContext - Final subscriptionActive:", subscriptionActive);

            setHasActiveSubscription(subscriptionActive);
            console.log("AuthContext - Updated subscription status:", subscriptionActive);

            return subscriptionActive;
        } catch (error) {
            console.error("AuthContext - Error checking subscription status:", error);
            setHasActiveSubscription(false);
            return false;
        }
    };

    // Don't auto-check subscription here - let SubscriptionGuard handle it
    // This prevents duplicate API calls

    // Comprehensive cleanup function to clear all user data
    const clearAllUserData = () => {
        // Clear all cookies
        clearCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE);
        clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);
        clearCookie("is_email_verified");
        
        // Clear all sessionStorage
        sessionStorage.removeItem("userEmail");
        sessionStorage.clear();
        
        // Clear specific localStorage keys for auth, preserving app settings
        localStorage.removeItem("foodie_rememberMe");
        localStorage.removeItem("persist:root"); // Redux persist data
        // DO NOT call localStorage.clear() as it wipes app settings like printer/theme preferences
        
        // Reset all auth context state
        setAllowedPages([]);
        setLoadingUser(false);
        setIsLoggedIn(false);
        setHasActiveSubscription(false);
        
        // Clear Redux state
        dispatch(clearState());
        dispatch(clearSubscriptionData());
        
        console.log("Complete user data cleanup performed");
    };

    const onLogout = async () => {
        try {
            if (isLoggedIn) setLoadingUser(true);
            const res = await adminService.removeAdminJwt();
        } catch (ex) {
            console.log("Logout API call failed, proceeding with cleanup");
        } finally {
            // Use comprehensive cleanup function
            clearAllUserData();
        }
    };

    const getPermissions = () => {
        setLoadingUser(true);
        cancellablePromise(adminService.getCurrentAdmin())
            .then(res => {
                const extraPerm = [];

                // Save subscription data to Redux (same structure as login response)
                console.log("AuthContext - Complete profile/permissions API response:", res);
                console.log("AuthContext - Saving subscription data to Redux from getPermissions:", {
                    subscriptionStatus: res?.subscriptionStatus || res?.subscriptionInfo, // Use subscriptionInfo as fallback
                    subscriptionInfo: res?.subscriptionInfo,
                    subscriptionRequired: res?.subscriptionRequired,
                });

                dispatch(
                    setSubscriptionData({
                        subscriptionStatus: res?.subscriptionStatus || res?.subscriptionInfo, // Use subscriptionInfo as fallback for consistency
                        subscriptionInfo: res?.subscriptionInfo,
                        subscriptionRequired: res?.subscriptionRequired,
                    })
                );

                // Check subscription status when getting user permissions
                const subscriptionActive =
                    res?.subscriptionStatus?.canAccess ||
                    res?.subscriptionInfo?.canAccess ||
                    !res?.subscriptionRequired || // If subscriptionRequired is false, user has access
                    res?.hasActiveSubscription ||
                    res?.canAccess ||
                    res?.subscription?.active ||
                    false;
                setHasActiveSubscription(subscriptionActive);
                console.log("AuthContext - User permissions response:", res);
                console.log("AuthContext - Subscription status:", subscriptionActive);

                const hasBusiness = res?.businessId;
                const hasStore = res?.storeId || (res?.storeIds && res?.storeIds.length > 0);

                if (!hasBusiness && !hasStore) {
                    extraPerm.unshift("business-setup");
                }

                if (!hasBusiness && !hasStore) {
                    setDashboardModal(true);
                } else {
                    setDashboardModal(false);
                }

                setAllowedPages([
                    ...extraPerm,
                    ...res.permissions.filter(p => p.includes(".nav")).map(p => p.split(".")[0]),
                ]);

                setCookie(
                    process.env.REACT_APP_ALLOWED_PAGES_COOKIE,
                    JSON.stringify(res.permissions.filter(p => p.includes(".nav")).map(p => p.split(".")[0]))
                );

                // Save user data to Redux for persistence
                dispatch(setBusinessId(res?.businessId));
                dispatch(setDefaultStore(res?.storeId || (res?.storeIds && res?.storeIds[0])));
                dispatch(setUser(res));
                setLoadingUser(false);

                // If user is logged in but has no subscription, they should be redirected
                if (!subscriptionActive && isLoggedIn) {
                    console.log("AuthContext - User has no active subscription");
                }
            })
            .catch(err => {
                // setAllowedPages(["no-permissions"]);
                setAllowedPages([]);
                setCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE, JSON.stringify([]));
                //   setCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE, JSON.stringify(['no-permissions']));
                setLoadingUser(false);
                Toast({
                    type: "error",
                    message: err.message,
                });
            });
    };

    useEffect(() => {
        if (isLoggedIn && hasActiveSubscription) {
            // Only get permissions if user has active subscription
            // Users without subscription should go to pricing first
            console.log("AuthContext - Getting permissions for subscribed user");
            getPermissions();
        } else if (isLoggedIn && !hasActiveSubscription) {
            getPermissions();
            console.log("AuthContext - Skipping getPermissions for user without subscription");
        }
    }, [isLoggedIn, reFetch, hasActiveSubscription]);

    const onLogin = async ({email, password, rememberMe, navigate}) => {
        setLoadingUser(true);
        setLoading(true);
        try {
            const res = await authService.login({
                email,
                password,
            });
            if (res?.token) {
                setCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE, res.token);
            }

            // ALWAYS save subscription data to Redux from login response
            console.log("AuthContext - Saving subscription data to Redux:", {
                subscriptionStatus: res?.user?.subscriptionStatus,
                subscriptionInfo: res?.subscriptionInfo,
                subscriptionRequired: res?.subscriptionRequired,
            });

            dispatch(
                setSubscriptionData({
                    subscriptionStatus: res?.user?.subscriptionStatus,
                    subscriptionInfo: res?.subscriptionInfo,
                    subscriptionRequired: res?.subscriptionRequired,
                })
            );

            // Handle cases where user needs subscription or business setup
            // Check for subscription required (can be success: true with subscriptionRequired: true)
            if (
                res?.subscriptionRequired ||
                (res?.user?.subscriptionStatus && !res?.user?.subscriptionStatus?.canAccess)
            ) {
                const subscriptionStatus = res.user?.subscriptionStatus || res.subscriptionInfo;

                console.log("AuthContext - User needs subscription/setup, redirecting...");
                console.log("AuthContext - subscriptionStatus:", subscriptionStatus);

                setIsLoggedIn(true); // Set logged in status
                setLoadingUser(false);
                setLoading(false);

                // Handle different redirect scenarios
                if (subscriptionStatus.redirectTo === "pricing") {
                    console.log("AuthContext - Redirecting to pricing page");
                    // Navigate to pricing page with businessId and businessName
                    const businessId = subscriptionStatus.businessId || "";
                    const businessName = subscriptionStatus.businessName || "";
                    navigate(`/pricing?businessId=${businessId}&businessName=${encodeURIComponent(businessName)}`);
                    Toast({
                        type: "info",
                        message: subscriptionStatus.message || "Please choose a subscription plan to continue",
                    });
                } else if (subscriptionStatus.redirectTo === "business-setup") {
                    console.log("AuthContext - Redirecting to business setup");
                    navigate("/business-setup");
                    Toast({
                        type: "info",
                        message: subscriptionStatus.message || "Please complete your business setup",
                    });
                } else {
                    console.log("AuthContext - Unknown redirect scenario");
                    // Generic error handling
                    Toast({
                        type: "error",
                        message: subscriptionStatus.message || "Unable to access dashboard. Please contact support.",
                    });
                }
                return;
            }

            // Handle successful login - check if user needs email verification
            if (!res?.user?.is_email_verified) {
                setCookie("is_email_verified", JSON.stringify(res?.user?.is_email_verified ? true : false));
                sessionStorage.setItem("userEmail", email);
                navigate("/signup-verify-otp");
                return;
            }

            // Handle case where business setup is needed (legacy check)
            if (!res?.user?.businessId && !res?.user?.storeId) {
                setIsLoggedIn(true);
                setCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE, res?.token);
                Toast({type: "success", message: "Login Successfully"});
                setLoadingUser(false);
                setLoading(false);
                navigate("/business-setup");
                return;
            }

            // Handle case where user verification is pending
            if (!res?.user?.is_verified) {
                navigate("/thank-you");
                return;
            }

            // Successful login with full access
            dispatch(setBusinessId(res?.user?.businessId));
            dispatch(setUserType(res?.user?.userType));
            dispatch(setDefaultStore(res?.user?.storeId));

            // Save user data to Redux for persistence (including profile info)
            dispatch(setUser(res?.user));

            // Set subscription status from login response
            const subscriptionActive =
                res?.user?.subscriptionStatus?.canAccess ||
                res?.subscriptionInfo?.canAccess ||
                !res?.subscriptionRequired || // If subscriptionRequired is false, user has access
                false;

            console.log("AuthContext - Login response:", res);
            console.log("AuthContext - subscriptionRequired:", res?.subscriptionRequired);
            console.log("AuthContext - subscriptionInfo:", res?.subscriptionInfo);
            console.log("AuthContext - Subscription status detected:", subscriptionActive);
            setHasActiveSubscription(subscriptionActive);

            setIsLoggedIn(true);
            setCookie(process.env.REACT_APP_FOODIE_TOKEN_COOKIE, res?.token);

            navigate("/dashboard");
            Toast({type: "success", message: "Login Successfully"});

            setLoadingUser(false);
            setLoading(false);

            // Handle remember me functionality
            if (rememberMe) {
                const authUserData = JSON.stringify({
                    foodie_user_email: email,
                    foodie_user_rememberMe: rememberMe,
                });
                localStorage.setItem("foodie_rememberMe", authUserData);
            } else {
                localStorage.removeItem("foodie_rememberMe");
            }
        } catch ({message}) {
            setIsLoggedIn(false);
            setLoadingUser(false);
            Toast({type: "error", message});
        } finally {
            setLoading(false);
        }
    };

    const hasPermission = perm => !user?.permissions?.length || user?.permissions?.includes(perm);

    return (
        <AuthContext.Provider
            value={{
                setIsLoggedIn,
                onLogout,
                onLogin,
                clearAllUserData, // Add cleanup function to context
                refetch: () => setRefetch(_ => !_),
                setLoading,
                hasPermission,
                allowedPages,
                loading,
                isLoggedIn,
                fetch: reFetch,
                user,
                loading_user,
                selectedStore,
                setSelectedStore,
                dashboardModal,
                hasActiveSubscription,
                setHasActiveSubscription,
                checkSubscriptionStatus,
            }}>
            {props.children}
        </AuthContext.Provider>
    );
}
export const useAuthContext = () => {
    return useContext(AuthContext);
};
